import { Check, ChevronDown, MessagesSquare } from "lucide-react";
import * as React from "react";
import { Navigate, NavLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  conversationsReducer, conversationTitle, INBOXES, inboxPath, isInboxId, ORGANIZING_KEPT, recordConversation,
  serverInbox, teamMembers, textConversation, unreadCount, visibleConversations, type ActivityType, type Channel, type Conversation, type InboxId, type InboxSort, type InboxTab, type Person,
  type UnreadAtOpen,
} from "@/lib/conversations";
import {
  leadNotFound, textsMissing, useContact, useContactEmailCompose, useReadConversation, useTextConversation, useTextConversationPages,
  useTextConversations, useTextInboxes,
} from "@/lib/queries";
import { cn } from "@/lib/utils";
import { ContactPanel } from "@/features/conversations/contact-panel";
import { InboxList, type ListPage } from "@/features/conversations/inbox-list";
import { Thread } from "@/features/conversations/thread";
import { seesCampaigns } from "@/lib/types";

/**
 * Conversations: the team's inbox of every exchange with a contact.
 *
 * Inboxes (`/conversations/:inbox`): the Team Inbox, everything the signed-in person may read; and
 * the personal ones in a menu beside it — All and Assigned to Me, both core-api's `assigned` for now
 * (it already takes texts to your own number). Followed by Me waits until following is kept.
 *
 * The threads are core-api's conversations (`GET /conversations`, ADR-095). The list is paged and
 * loads on as it is scrolled, so the inbox, the tabs, the search, the sort and the filter are all
 * the server's (ACL-593): each page continues the one before it. It is refetched every 30 seconds
 * and on the `messages` hint. Opening one reads it for everybody who shares its inbox; Mark as
 * unread puts it back. Read and unread and the assignee are all core-api keeps: star, followers,
 * tags, owner, do-not-disturb and delete are not offered (`ORGANIZING_KEPT`).
 *
 * A conversation exists only once the first text arrives. `?c=<conversation id>` opens one;
 * `?lead=<lead id>` opens a contact's thread from their record when they have none — the contact
 * page links there — and becomes `?c=` once they do. A contact with no texts is in no list, and
 * nothing here creates an empty conversation.
 */
export function ConversationsPage() {
  const { inbox } = useParams();
  if (!isInboxId(inbox)) return <Navigate to={inboxPath("team")} replace />;
  return <LiveConversations inbox={inbox} />;
}

function LiveConversations({ inbox }: { inbox: InboxId }) {
  const asRep = !seesCampaigns(useAuth().user?.role);
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = React.useState<ListQuery>({ tab: "all", search: "", sort: "newest", types: [] });
  const search = useDebounced(query.search.trim(), 300);
  // The list is paged: what it holds and its order are the server's, and further pages are loaded
  // as it is scrolled. Sorted or narrowed here, a page would be sorted against only what had loaded.
  const list = useTextConversationPages({
    inbox: serverInbox(inbox),
    unread: query.tab === "unread" || undefined,
    recent: query.tab === "recent" || undefined,
    q: search || undefined,
    sort: query.sort === "newest" ? undefined : query.sort,
    type: query.types.length > 0 ? [...query.types].sort().join(",") : undefined,
  });
  const read = useReadConversation();
  // A core-api without texts yet (ACL-103 not deployed to it): an empty inbox that says so, not an
  // error, and a contact's thread (`?lead=`) still opens.
  const missing = textsMissing(list.error);
  const loaded = React.useMemo(() => {
    if (missing) return [];
    if (!list.data) return undefined;
    // Pages are counted from the top of a list that moves: a text arriving between two pages pushes
    // a conversation down into the next one. Each is kept once, where it was first listed.
    const seen = new Set<string>();
    return list.data.pages
      .flatMap((p) => p.items)
      .filter((t) => !seen.has(t.id) && seen.add(t.id) !== undefined)
      .map((t) => textConversation(t, asRep));
  }, [missing, list.data]);

  // The open conversation when the list's page does not hold it: a link, or an older thread.
  const openId = params.get("c");
  const held = openId !== null && (loaded ?? []).some((c) => c.id === openId);
  const single = useTextConversation(openId !== null && !held && list.isSuccess ? openId : undefined);
  const extra = React.useMemo(() => (single.data ? textConversation(single.data, asRep) : null), [single.data, asRep]);

  // A contact's thread opens as their conversation once they have one (`?c=`), and as their record
  // until then (`?lead=`): a conversation exists only after the first text. Opened for an email (the
  // record's Email button, `channel=email`), it is the email thread core-api would send in, else the
  // record's thread, where the first email starts one; otherwise their newest thread that is not an
  // email thread, so texting from the record is as it was (ADR-180).
  const leadId = params.get("lead");
  const forEmail = params.get("channel") === "email";
  const theirs = useTextConversations({ lead_id: leadId ?? undefined }, 0, {
    enabled: leadId !== null && !forEmail && list.isSuccess,
  });
  const emailCompose = useContactEmailCompose(leadId !== null && forEmail && list.isSuccess ? leadId : undefined);
  const conversationId =
    leadId === null
      ? undefined
      : forEmail
        ? emailCompose.data?.conversation_id ?? undefined
        : theirs.data?.items.find((t) => t.channel !== "email")?.id;
  React.useEffect(() => {
    if (!conversationId) return;
    const p = new URLSearchParams(params);
    p.delete("lead");
    p.set("c", conversationId);
    setParams(p, { replace: true });
  }, [conversationId, params, setParams]);

  // Asked once the list has answered: a server without texts is asked nothing more.
  const unread = useInboxUnread(inbox, list.isSuccess);

  return (
    <ConversationsView
      inbox={inbox}
      loaded={loaded}
      extra={extra}
      extraLoading={single.isLoading}
      loadError={list.isError && !missing ? list.error : null}
      texts={!missing}
      live
      unreadBadge={unread}
      page={
        loaded && !missing
          ? {
              pages: list.data?.pages.length ?? 0,
              // Not while a new sort or filter is still answering: the list shown is the last one,
              // and its next page is not the next page of the one coming.
              more: list.hasNextPage && !list.isPlaceholderData,
              loading: list.isFetchingNextPage,
              failed: list.isFetchNextPageError,
              onMore: () => void list.fetchNextPage(),
            }
          : undefined
      }
      onQuery={setQuery}
      onServerRead={(id, isRead, undo) =>
        read.mutate(
          { id, read: isRead },
          {
            // The page showed it already. A refusal (a 403 for somebody who may not open it, a 500)
            // would leave the row wrong until a reload: the next refetch brings the same list, so
            // nothing corrects it. Put it back, and say so.
            onError: (e) => {
              undo();
              toast.error(
                isRead ? "Could not mark the conversation as read" : "Could not mark the conversation as unread",
                e instanceof ApiError ? e.message : "Please try again.",
              );
            },
          },
        )
      }
    />
  );
}

/**
 * The Unread tab's count, from core-api. Assigned to Me (and All, which is it for now) is its
 * inbox's count in `GET /conversations/inboxes`. The Team Inbox is every inbox you read, and those
 * overlap (a rep's thread shared into its campaign is in both), so adding them up would count it
 * twice: it is the unread list's total instead.
 */
function useInboxUnread(inbox: InboxId, enabled: boolean): number | undefined {
  const team = inbox === "team";
  const inboxes = useTextInboxes({ enabled: enabled && !team });
  const unreadList = useTextConversations({ unread: true }, 0, { enabled: enabled && team });
  if (!enabled) return 0;
  if (team) return unreadList.data?.total ?? unreadList.data?.items.length;
  return inboxes.data?.find((i) => i.id === serverInbox(inbox))?.unread;
}

/** What the inbox is showing, for a server that narrows and orders the list itself. */
interface ListQuery {
  tab: InboxTab;
  search: string;
  sort: InboxSort;
  types: ActivityType[];
}

/** `m` without `id`; `m` itself when it has none, so nothing renders again for it. */
function without<T>(m: Record<string, T>, id: string): Record<string, T> {
  if (!(id in m)) return m;
  const rest = { ...m };
  delete rest[id];
  return rest;
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * The inbox itself. `loaded` is core-api's conversations, undefined while they load; `initial` is
 * what it opens with (the tests' threads). Read, unread, star, delete and the rest change the page's
 * own copy at once; `onServerRead` tells core-api about reading.
 */
export function ConversationsView({
  inbox, initial = [], loaded, extra = null, extraLoading = false, page, loadError = null, texts = true, live = false, unreadBadge,
  onQuery, onServerRead,
}: {
  inbox: InboxId;
  /** The open conversation, when `loaded` does not hold it. */
  extra?: Conversation | null;
  /** `extra` is being asked for: the open thread stays as it was meanwhile. */
  extraLoading?: boolean;
  /** The Unread tab's count, when the server keeps it; else counted from what the page holds. */
  unreadBadge?: number;
  /** The inbox's paging: the next page loads as the list nears its end. */
  page?: ListPage;
  /** The tab, search, sort and filter, for a server that narrows and orders the list itself. */
  onQuery?: (query: ListQuery) => void;
  /** Whether the server keeps texts: without, the inbox says so and no thread offers them. */
  texts?: boolean;
  initial?: Conversation[];
  loaded?: Conversation[];
  loadError?: unknown;
  /** The threads are core-api's: each reads its own texts when opened. */
  live?: boolean;
  /** Tells core-api about a read or an unread the page has already shown; `undo` puts the
   *  conversation's unread count back if core-api refuses it. */
  onServerRead?: (id: string, read: boolean, undo: () => void) => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const me: Person = React.useMemo(
    () => ({ id: String(user?.id ?? "me"), name: user?.display_name || user?.email || "You" }),
    [user?.id, user?.display_name, user?.email],
  );
  const [conversations, dispatch] = React.useReducer(conversationsReducer, initial);
  React.useEffect(() => {
    if (loaded) dispatch({ type: "load", conversations: loaded });
  }, [loaded]);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = React.useState<InboxTab>("all");
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState<InboxSort>("newest");
  const [types, setTypes] = React.useState<ActivityType[]>([]);
  const [panelOpen, setPanelOpen] = React.useState(true);
  // Where the "New" line goes in each thread: what was unread when it was opened. Opening reads it,
  // so the count has to be taken first. Opened again with nothing unread, the line goes.
  const [newFrom, setNewFrom] = React.useState<Record<string, number | null>>({});
  // The same for a thread read from core-api: how many texts were unread, and the newest text then.
  const [unreadAtOpen, setUnreadAtOpen] = React.useState<Record<string, UnreadAtOpen>>({});

  const openId = params.get("c");
  const leadId = params.get("lead") ?? undefined;
  const record = useContact(leadId);
  const fromRecord = React.useMemo(() => (record.data ? recordConversation(record.data) : null), [record.data]);
  // The thread last shown. When the open conversation leaves the list's page — the Unread tab drops
  // it once it is read, a search stops matching it — it is asked for on its own (`extra`); until that
  // answers, the thread keeps showing the conversation as it was, so it stays mounted and keeps its
  // filter, its scroll and a draft in the composer.
  const lastOpen = React.useRef<Conversation | null>(null);
  // Opened for an email, a contact's thread is the record's until core-api names their email
  // thread; otherwise never one of their email threads, which the Email button opens.
  const preferChannel: Channel | undefined = params.get("channel") === "email" ? "email" : undefined;
  const open = leadId
    ? (preferChannel === "email"
        ? null
        : conversations.find((c) => c.leadId === leadId && c.deletedAt === null && c.channel !== "email")) ??
      fromRecord
    : conversations.find((c) => c.id === openId && c.deletedAt === null) ??
      (extra && extra.id === openId ? extra : null) ??
      (extraLoading && lastOpen.current?.id === openId ? lastOpen.current : null);
  lastOpen.current = open;
  // core-api scopes who may read a conversation and who may open its lead by separate rules
  // (`visible_conversations`, `visible_leads`): a rep can read the texts of a lead that is on none
  // of their campaigns' lists, and the record then answers 404. That thread is drawn as one with no
  // record, from the conversation's own texts, with nothing that needs the record: its links,
  // comments, Set outcome. Shared with the thread's own read of the record, so it asks once.
  const openRecord = useContact(open?.leadId ?? undefined);
  const recordShut = open !== null && open.leadId !== null && leadNotFound(openRecord.error);
  const thread = React.useMemo(
    () => (open !== null && recordShut ? { ...open, leadId: null } : open),
    [open, recordShut],
  );
  // core-api's list comes narrowed to the inbox, the search and the filter already, and in the order
  // asked for; the page does not narrow or sort it again (its matching is not the server's, and would
  // drop what the server found, and a re-sort would move rows above the ones scrolled past). The tab
  // still applies, so a thread read on the Unread tab leaves it at once rather than at the refetch.
  const shown = visibleConversations(conversations, {
    inbox: live ? "team" : inbox,
    tab,
    search: live ? "" : search,
    sort: live ? null : sort,
    types: live ? [] : types,
    meId: me.id,
    now: Date.now(),
  });
  const organize = !live || ORGANIZING_KEPT;
  React.useEffect(() => {
    onQuery?.({ tab, search, sort, types });
  }, [onQuery, tab, search, sort, types]);
  const meta = INBOXES.find((i) => i.id === inbox)!;

  // A thread opened, from the list or a link, is read. Once per opening: keyed on the id, so Mark
  // as unread on the open thread stays unread instead of being read again at once.
  const openRef = React.useRef(open);
  openRef.current = open;
  const openedId = open?.id ?? null;
  React.useEffect(() => {
    const c = openRef.current;
    if (!c) return;
    if (c.unread === 0) {
      setNewFrom((m) => without(m, c.id));
      setUnreadAtOpen((m) => without(m, c.id));
      return;
    }
    setNewFrom((m) => ({ ...m, [c.id]: c.activities.length - c.unread }));
    const newest = c.activities.findLast((a) => a.kind === "message")?.at ?? null;
    setUnreadAtOpen((m) => ({ ...m, [c.id]: { unread: c.unread, newest } }));
    dispatch({ type: "open", id: c.id });
    serverRead(c, true);
  }, [openedId]);

  /** Tells core-api, with the conversation as it was before the page changed it: what to put back. */
  const serverRead = (before: Conversation, read: boolean) =>
    onServerRead?.(before.id, read, () => dispatch({ type: "unread", id: before.id, unread: before.unread }));

  const select = (id: string | null) => {
    const p = new URLSearchParams(params);
    p.delete("lead");
    // The channel a link opened the page on is that thread's: the next one opened starts on its own.
    p.delete("channel");
    if (id) p.set("c", id);
    else p.delete("c");
    setParams(p, { replace: true });
  };

  return (
    <div className="flex h-full min-h-[34rem] flex-col">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border bg-surface px-4 pt-3">
        <h1 className="pb-3 text-lg font-semibold tracking-tight text-text">Conversations</h1>
        <nav className="flex flex-wrap items-end gap-1 text-sm" aria-label="Inboxes">
          <InboxLink id="team" />
          <PersonalInboxMenu inbox={inbox} />
        </nav>
      </div>

      {/* `overflow-hidden` because this is the containing block of every `sr-only` label in the list,
          the thread and the contact panel, none of whose scrollers is positioned. Each is placed at
          its spot in its scroller's content — a long panel's last field is far below the fold — and
          without the clip they push the app frame past the screen: it scrolls, the header goes off
          the top and blank canvas shows under the composer (ACL-454). */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden bg-surface">
        {leadId && !open ? (
          record.isError ? (
            <div className="flex-1 p-4">
              <ErrorState error={record.error} title="Could not open this contact's thread" />
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center" role="status">
              <Spinner className="size-5" />
              <span className="sr-only">Opening the thread</span>
            </div>
          )
        ) : conversations.length === 0 && !open && loadError ? (
          <div className="flex-1 p-4">
            <ErrorState error={loadError} title="Could not load the conversations" />
          </div>
        ) : conversations.length === 0 && !open && live && loaded === undefined && tab === "all" && !search && types.length === 0 ? (
          <div className="flex flex-1 items-center justify-center" role="status">
            <Spinner className="size-5" />
            <span className="sr-only">Loading conversations</span>
          </div>
        ) : conversations.length === 0 && !open && tab === "all" && !search && types.length === 0 ? (
          <EmptyState
            className="flex-1"
            icon={MessagesSquare}
            title={texts ? "No conversations yet" : "Texts aren't on this server yet"}
            description={
              texts
                ? "A text to one of your numbers starts one. Calls, notes and outcomes with the contact show up beside it."
                : "This core-api does not keep texts, so there are no conversations to list. A contact's thread still opens from their record."
            }
          />
        ) : (
          <>
            <InboxList
              title={meta.title}
              conversations={shown}
              unread={unreadBadge ?? unreadCount(conversations, inbox, me.id)}
              page={page}
              tab={tab}
              onTab={setTab}
              search={search}
              onSearch={setSearch}
              sort={sort}
              onSort={setSort}
              types={types}
              onTypes={setTypes}
              openId={open?.id ?? null}
              onOpen={select}
              onStar={(id, starred) => dispatch({ type: "star", id, starred })}
              organize={organize}
              onBulk={(ids, action) => {
                const at = new Date().toISOString();
                for (const id of ids) {
                  if (action === "delete") dispatch({ type: "delete", id, at });
                  else if (action === "star") dispatch({ type: "star", id, starred: true });
                  else {
                    const before = conversations.find((c) => c.id === id);
                    dispatch({ type: "read", id, read: action === "read" });
                    if (before) serverRead(before, action === "read");
                  }
                }
                if (action === "delete") {
                  if (open && ids.includes(open.id)) select(null);
                  toast.success(
                    `${ids.length} conversation${ids.length === 1 ? "" : "s"} deleted`,
                    "They are out of the inbox. The history is kept.",
                  );
                }
              }}
              // On a narrow screen the list gives way to the open thread.
              className={cn(open && "hidden lg:flex")}
            />
            {open ? (
              <>
                <Thread
                  conversation={thread ?? open}
                  live={live && !open.id.startsWith("lead:")}
                  texts={texts}
                  organize={organize}
                  newFrom={newFrom[open.id] ?? null}
                  unreadAtOpen={unreadAtOpen[open.id]}
                  preferChannel={preferChannel}
                  panelOpen={panelOpen}
                  onPanel={setPanelOpen}
                  onBack={() => select(null)}
                  onStar={(starred) => dispatch({ type: "star", id: open.id, starred })}
                  onRead={(read) => {
                    dispatch({ type: "read", id: open.id, read });
                    if (!open.id.startsWith("lead:")) serverRead(open, read);
                  }}
                  onDelete={() => {
                    dispatch({ type: "delete", id: open.id, at: new Date().toISOString() });
                    select(null);
                    toast.success(
                      "Conversation deleted",
                      `${conversationTitle(open)} is out of the inbox. The history is kept.`,
                    );
                  }}
                />
                {panelOpen && (
                  <>
                    {/* Below the widest layout the panel is a drawer over the thread. */}
                    <button
                      type="button"
                      className="absolute inset-0 z-10 bg-black/20 xl:hidden"
                      aria-label="Close contact details"
                      onClick={() => setPanelOpen(false)}
                    />
                    <ContactPanel
                      conversation={thread ?? open}
                      members={teamMembers(conversations, me)}
                      onClose={() => setPanelOpen(false)}
                      onTag={(tag, on) => dispatch({ type: "tag", id: open.id, tag, on })}
                      onOwner={(owner) => dispatch({ type: "owner", id: open.id, owner })}
                      onFollower={(person, on) => dispatch({ type: "follower", id: open.id, person, on })}
                      onDnd={(key, on) => dispatch({ type: "dnd", id: open.id, key, on })}
                      organize={organize}
                      className="absolute inset-y-0 right-0 z-20 max-w-[22rem] shadow-lg xl:static xl:z-auto xl:max-w-none xl:shadow-none"
                    />
                  </>
                )}
              </>
            ) : (
              <EmptyState
                className="hidden flex-1 bg-surface-sunken lg:flex"
                icon={MessagesSquare}
                title="Select a conversation"
                description="Pick a thread on the left to see everything that passed with that contact."
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InboxLink({ id }: { id: InboxId }) {
  const meta = INBOXES.find((i) => i.id === id)!;
  return (
    <NavLink
      to={inboxPath(id)}
      title={meta.description}
      className={({ isActive }) =>
        cn(
          "border-b-2 px-2 pb-2.5 font-medium transition-colors",
          isActive ? "border-accent-9 text-accent-11" : "border-transparent text-text-muted hover:text-text",
        )
      }
    >
      {meta.label}
    </NavLink>
  );
}

// Followed by Me waits until following is kept (ACL-335).
const PERSONAL_INBOXES = ["mine", "assigned"] as const satisfies readonly InboxId[];

/** The personal inboxes, in one menu beside the Team Inbox: it reads as the tab you are on, named
 *  for the one picked. */
function PersonalInboxMenu({ inbox }: { inbox: InboxId }) {
  const navigate = useNavigate();
  const current = PERSONAL_INBOXES.find((id) => id === inbox);
  const label = current ? INBOXES.find((i) => i.id === current)!.label : null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label ? `Personal Inbox: ${label}` : "Personal Inbox"}
          className={cn(
            "flex items-center gap-1 border-b-2 px-2 pb-2.5 font-medium transition-colors",
            current ? "border-accent-9 text-accent-11" : "border-transparent text-text-muted hover:text-text",
          )}
        >
          Personal Inbox
          {label && <span className="font-normal">· {label}</span>}
          <ChevronDown className="size-3.5" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {PERSONAL_INBOXES.map((id) => {
          const meta = INBOXES.find((i) => i.id === id)!;
          return (
            <DropdownMenuItem key={id} title={meta.description} onSelect={() => navigate(inboxPath(id))}>
              <Check className={cn(id !== current && "invisible")} />
              {meta.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
